import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { getCookie } from "../../utility/getCookie";
import {
  AuthState,
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  RegisterResponse,
  ProfileResponse,
  EmailRequest,
  BaseResponse,
  ForgotPasswordRequest,
  ChangeTransactionPasswordRequest,
  ChangePasswordRequest,
  VerifyPasswordRequest,
  VerifyPasswordResponse,
  RefreshTokenRequest,
  UpdateProfileRequest,
  ProfilePictureRequest,
} from "./types";
import { setCookie } from "../../utility/setCookie";
import { apiErrorMessage } from "../../utility/apiError";
import { toast } from "react-toastify";
const AUTH_BASE_URL = "https://devapi.salar.in";

const API_URL = "https://devapi.salar.in/api/v1/customer";

// const API_URL =process.env.REACT_APP_CUSTOMER_API_BASE_URL

if (!process.env.REACT_APP_API_BASE_URL) {
  console.warn("Warning: REACT_APP_API_BASE_URL is not defined.");
}
if (!process.env.REACT_APP_CUSTOMER_APP_BACKEND_PREFIX) {
  console.warn(
    "Warning: REACT_APP_CUSTOMER_APP_BACKEND_PREFIX is not defined.",
  );
}

// Axios instance with cookies support
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// // Helper to refresh token
// const refreshAccessToken = async () => {
//   try {
//     const refreshToken = getCookie("refresh_token");
//     if (!refreshToken) throw new Error("No refresh token");

//     const response = await axios.post(`${API_URL}/auth/refresh`, {
//       refreshToken,
//     });
//     setCookie("access_token", response.data.accessToken, 7);
//     setCookie("refresh_token", response.data.refreshToken, 7);
//     // The backend sets the cookie in AuthController, but we might need to handle it here if not automated
//     return response.data;
//   } catch (error) {
//     console.error("Token refresh failed", error);
//     throw error;
//   }
// };

// Helper: validate token is a real JWT, not undefined/null/"undefined"
const isValidToken = (token: string | null | undefined): token is string =>
  !!token && token !== "undefined" && token !== "null" && token.length > 20;

// Helper: save tokens only when they are valid
const saveAuthTokens = (accessToken: string, refreshToken: string) => {
  if (isValidToken(accessToken)) {
    setCookie("access_token", accessToken, 7);
    localStorage.setItem("access_token", accessToken);
  } else {
    console.error("[Auth] Registration/Login response missing accessToken — profile calls will fail.");
  }
  if (isValidToken(refreshToken)) {
    setCookie("refresh_token", refreshToken, 7);
    localStorage.setItem("refresh_token", refreshToken);
  }
};

// Request interceptor to add Bearer token
api.interceptors.request.use(
  (config) => {
    const raw = localStorage.getItem("access_token") || getCookie("access_token");
    const token = isValidToken(raw) ? raw : null;
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor — catch 401 so caller gets a clean rejection
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.warn("[Auth] 401 Unauthorized — token may be invalid or expired.");
    }
    return Promise.reject(error);
  },
);

// Async Thunks
export const login = createAsyncThunk(
  "customerAuth/login",
  async (data: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await api.post<AuthResponse>("/auth/login", data);
      const { accessToken, refreshToken } = response.data;
      saveAuthTokens(accessToken, refreshToken);
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        "Login failed";
      return rejectWithValue(message);
    }
  },
);

export const register = createAsyncThunk(
  "customerAuth/register",
  async (data: RegisterRequest, { rejectWithValue }) => {
    try {
      const response = await api.post<RegisterResponse>("/auth/register", data);
      const { accessToken, refreshToken } = response.data;
      saveAuthTokens(accessToken, refreshToken);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Registration failed",
      );
    }
  },
);

export const sendOtp = createAsyncThunk(
  "customerAuth/sendOtp",
  async (data: EmailRequest, { rejectWithValue }) => {
    try {
      const response = await api.post<BaseResponse>("/auth/otp", data);
      console.log("response", response);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send OTP",
      );
    }
  },
);

export const getProfile = createAsyncThunk(
  "customerAuth/getProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<ProfileResponse>("/auth/profile");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to fetch profile"),
      );
    }
  },
);

export const logout = createAsyncThunk(
  "customerAuth/logout",
  async (_, { rejectWithValue }) => {
    // Always clear local storage and cookies — even if API call fails
    const clearLocalAuth = () => {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      setCookie("access_token", "", -1);
      setCookie("refresh_token", "", -1);
    };

    try {
      const response = await api.post<BaseResponse>("/auth/logout");
      clearLocalAuth();
      return response.data;
    } catch (error: any) {
      // Still clear local tokens so the user is logged out on the client side
      clearLocalAuth();
      return rejectWithValue(apiErrorMessage(error, "Logout failed"));
    }
  },
);

export const forgotPassword = createAsyncThunk(
  "customerAuth/forgotPassword",
  async (data: ForgotPasswordRequest, { rejectWithValue }) => {
    try {
      const response = await api.post<BaseResponse>(
        "/auth/forgot-password",
        data,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Forgot password operation failed"),
      );
    }
  },
);

export const changeTransactionPassword = createAsyncThunk(
  "customerAuth/changeTransactionPassword",
  async (data: ChangeTransactionPasswordRequest, { rejectWithValue }) => {
    try {
      const response = await api.post<BaseResponse>(
        "/auth/change-transaction-password",
        data,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to change transaction password"),
      );
    }
  },
);

export const verifyCurrentPassword = createAsyncThunk(
  "customerAuth/verifyCurrentPassword",
  async (data: VerifyPasswordRequest, { rejectWithValue }) => {
    try {
      const response = await api.post<VerifyPasswordResponse>(
        "/auth/verify-password",
        data,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to verify current password"),
      );
    }
  },
);

export const changePassword = createAsyncThunk(
  "customerAuth/changePassword",
  async (data: ChangePasswordRequest, { rejectWithValue }) => {
    try {
      const response = await api.post<BaseResponse>("/auth/change-password", data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to change password"),
      );
    }
  },
);

export const updateUserProfile = createAsyncThunk<
  ProfileResponse,
  UpdateProfileRequest
>(
  "customerAuth/updateUserProfile",
  async (data: UpdateProfileRequest, { rejectWithValue }) => {
    try {
      const response = await api.post<ProfileResponse>(
        `/auth/update-profile`,
        data.data,
      );
      return response.data;
    } catch (error: any) {
      const msg = apiErrorMessage(error, "Failed to update profile");
      if (msg) {
        toast.error(msg, {toastId: "update-profile-error"});
      }
      return rejectWithValue(msg);
    }
  },
);

export const addProfilePicture = createAsyncThunk<
  ProfileResponse,
  ProfilePictureRequest
>(
  "customerAuth/addProfilePicture",
  async (data: ProfilePictureRequest, { rejectWithValue }) => {
    try {
      const response = await api.put<ProfileResponse>(
        `/auth/add-profile-picture?profilePicture=${encodeURIComponent(
          data.profilePicture,
        )}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to add profile picture"),
      );
    }
  },
);

const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  authloading: false,
  autherror: null,
  profileLoading: false,
  profileError: null,
  success: false,
  message: null,
};

const authSlice = createSlice({
  name: "customerAuth",
  initialState,
  reducers: {
    clearState: (state) => {
      state.authloading = false;
      state.autherror = null;
      state.success = false;
      state.message = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.authloading = true;
        state.autherror = null;
      })
      .addCase(
        login.fulfilled,
        (state, action: PayloadAction<AuthResponse>) => {
          state.authloading = false;
          state.success = true;
          state.user = action.payload.data || null;
          state.accessToken = action.payload.accessToken ?? null;
          state.refreshToken = action.payload.refreshToken ?? null;
          state.message = action.payload.message ?? null;
        },
      )
      .addCase(login.rejected, (state, action) => {
        state.authloading = false;
        state.autherror = action.payload as string;
      })
      // Register
      .addCase(register.pending, (state) => {
        state.authloading = true;
        state.autherror = null;
      })
      .addCase(
        register.fulfilled,
        (state, action: PayloadAction<RegisterResponse>) => {
          state.authloading = false;
          state.success = true;
          state.message = action.payload.message ?? null;
          state.user = action.payload.data?.customer || null;
          state.accessToken = action.payload.accessToken ?? null;
          state.refreshToken = action.payload.refreshToken ?? null;
        },
      )
      .addCase(register.rejected, (state, action) => {
        state.authloading = false;
        state.autherror = action.payload as string;
      })
      // Get Profile
      .addCase(getProfile.pending, (state) => {
        state.profileLoading = true;
      })
      .addCase(
        getProfile.fulfilled,
        (state, action: PayloadAction<ProfileResponse>) => {
          state.profileLoading = false;
          state.user = action.payload.data;
        },
      )
      .addCase(getProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.profileError = action.payload as string;
        console.error("[Auth] getProfile failed:", action.payload);
      })
      // Update Profile
      .addCase(
        updateUserProfile.fulfilled,
        (state, action: PayloadAction<ProfileResponse>) => {
          state.authloading = false;
          state.success = true;
          state.message = action.payload.message;
          state.user = action.payload.data;
        },
      )
      // Add Profile Picture
      .addCase(
        addProfilePicture.fulfilled,
        (state, action: PayloadAction<ProfileResponse>) => {
          state.authloading = false;
          state.success = true;
          state.message = action.payload.message;
          state.user = action.payload.data;
        },
      )
      // Logout — clear user state whether API succeeds or fails
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.success = true;
      })
      .addCase(logout.rejected, (state) => {
        // API failed but local tokens are already cleared — reset user state anyway
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
      })
      // Common for others
      .addMatcher(
        (action) =>
          action.type.endsWith("/pending") &&
          !action.type.includes("getProfile") &&
          !action.type.includes("login") &&
          !action.type.includes("register") &&
          !action.type.includes("updateUserProfile") &&
          !action.type.includes("addProfilePicture"),
        (state) => {
          state.authloading = true;
          state.autherror = null;
          state.success = false;
        },
      )
      .addMatcher(
        (action) =>
          action.type.endsWith("/fulfilled") &&
          !action.type.includes("getProfile") &&
          !action.type.includes("login") &&
          !action.type.includes("register") &&
          !action.type.includes("updateUserProfile") &&
          !action.type.includes("addProfilePicture"),
        (state, action: PayloadAction<BaseResponse>) => {
          state.authloading = false;
          state.success = true;
          state.message = action.payload.message;
        },
      )
      .addMatcher(
        (action) =>
          action.type.endsWith("/rejected") &&
          !action.type.includes("getProfile") &&
          !action.type.includes("login") &&
          !action.type.includes("register") &&
          !action.type.includes("updateUserProfile") &&
          !action.type.includes("addProfilePicture"),
        (state, action: PayloadAction<any>) => {
          state.authloading = false;
          state.autherror = action.payload as string;
        },
      );
  },
});

export const { clearState } = authSlice.actions;
export default authSlice.reducer;
